import { Recipe } from "../models/recipe";
import { Ingredient } from "../models/ingredient";
import { MethodStep } from "../models/method-step";
import { getQuantityString } from "./units";
import { formatDuration } from "./duration-utils";

// Composes a readable plain-text version of a recipe for the OS share sheet.
// Works offline and for any recipe (user-created or imported); the source URL
// is appended only when present.
export function buildShareText(
  recipe: Recipe,
  ingredients: Ingredient[],
  methodSteps: MethodStep[],
): string {
  const lines: string[] = [recipe.title?.trim() || "Recipe"];

  // Meta: difficulty · Serves N · duration
  const meta: string[] = [];
  if (recipe.difficulty) meta.push(recipe.difficulty);
  if (recipe.servings) meta.push(`Serves ${recipe.servings}`);
  if (recipe.cookingDuration) meta.push(formatDuration(recipe.cookingDuration));
  if (meta.length) lines.push(meta.join(" · "));

  // Ingredients — group rows with a name become headers; nameless groups are skipped.
  const ingredientLines = ingredients
    .filter((i) => !i.deletedAt)
    .map((i) => {
      if (i.group) return i.name ? `\n${i.name}` : null;
      const qty = getQuantityString(i);
      const subQty = i.substitute ? getQuantityString(i.substitute) : "";
      const sub = i.substitute
        ? ` (or ${subQty ? `${subQty} ` : ""}${i.substitute.name})`
        : "";
      return `• ${qty ? `${qty} ` : ""}${i.name ?? ""}${sub}`.trimEnd();
    })
    .filter((line): line is string => Boolean(line));
  if (ingredientLines.length) {
    lines.push("", "INGREDIENTS", ...ingredientLines);
  }

  // Method — group rows become headers; real steps are numbered (headings skipped).
  const steps = methodSteps.filter((s) => !s.deletedAt);
  if (steps.length) {
    lines.push("", "METHOD");
    let stepNo = 0;
    steps.forEach((s) => {
      if (s.group) {
        stepNo = 0; // restart numbering under each section heading
        if (s.instruction?.trim()) lines.push(`\n${s.instruction.trim()}`);
        return;
      }
      stepNo += 1;
      lines.push(`${stepNo}. ${s.instruction ?? ""}`);
    });
  }

  // Notes
  if (recipe.notes?.trim()) {
    lines.push("", "NOTES", recipe.notes.trim());
  }

  // Source attribution and/or link
  if (recipe.source?.trim()) {
    lines.push("", `Source: ${recipe.source.trim()}`);
    if (recipe.sourceUrl?.trim()) lines.push(recipe.sourceUrl.trim());
  } else if (recipe.sourceUrl?.trim()) {
    lines.push("", `Source: ${recipe.sourceUrl.trim()}`);
  }

  return lines.join("\n");
}
